import { useEffect, useState } from 'react'
import { AIGeneratorContext } from '../components/AIGeneratorModal/interfaces'

/**
 * Hook para acessar o contexto de geração de IA salvo no localStorage
 * Disponível em todas as tabs do projeto
 */
export const useAIGeneratorContext = (projectId: string | null) => {
  const [context, setContext] = useState<AIGeneratorContext | null>(null)
  
  const storageKey = `ai_generator_context_${projectId || 'new'}`
  
  // Carregar contexto inicial
  useEffect(() => {
    const loadContext = () => {
      const savedContext = localStorage.getItem(storageKey)
      if (savedContext) {
        try {
          const parsed: AIGeneratorContext = JSON.parse(savedContext)
          setContext(parsed)
        } catch (error) {
          console.error('Error loading AI context:', error)
        }
      }
    }
    
    loadContext()
    
    // Escutar mudanças no localStorage de outras abas
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey && e.newValue) {
        try {
          const parsed: AIGeneratorContext = JSON.parse(e.newValue)
          setContext(parsed)
        } catch (error) {
          console.error('Error parsing storage event:', error)
        }
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    
    // Escutar mudanças locais também (mesma aba)
    const handleLocalChange = () => {
      loadContext()
    }
    
    // Criar evento customizado para mudanças locais
    window.addEventListener('ai-context-updated', handleLocalChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('ai-context-updated', handleLocalChange)
    }
  }, [storageKey])
  
  // Função para limpar o contexto
  const clearContext = () => {
    localStorage.removeItem(storageKey)
    setContext(null)
    // Disparar evento para outras partes da aplicação
    window.dispatchEvent(new Event('ai-context-updated'))
  }
  
  // Função para atualizar o contexto
  const updateContext = (newContext: Partial<AIGeneratorContext>) => {
    const updatedContext = {
      ...context,
      ...newContext,
      timestamp: new Date().toISOString()
    } as AIGeneratorContext
    
    localStorage.setItem(storageKey, JSON.stringify(updatedContext))
    setContext(updatedContext)
    // Disparar evento para outras partes da aplicação
    window.dispatchEvent(new Event('ai-context-updated'))
  }
  
  return {
    context,
    clearContext,
    updateContext,
    hasGeneratedData: !!context?.generatedData,
    description: context?.description || '',
    schoolTermId: context?.schoolTermId || null,
    groupId: context?.groupId || null,
    generatedData: context?.generatedData || null
  }
}